import { useRef } from 'react'

import { useCreateCheckpoint } from '../hooks/useCreateCheckpoint'
import { useRouter } from 'next/compat/router'
import { SubmitHandler, useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'
import { MdQrCode } from 'react-icons/md'

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'

import { Button, FormInputControl } from '@/components/ui'

interface FormDataIProps {
  name: string
  facility: string
}

interface CreateCheckPointsModalIProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateCheckPointsModal = ({
  isOpen,
  onClose,
}: CreateCheckPointsModalIProps) => {
  const cancelRef = useRef()
  const { query } = useRouter()
  const selectedFloorId = Array.isArray(query?.floorId)
    ? query?.floorId[0]
    : query?.floorId
  const { register, watch, errors, handleSubmit } = useForm<FormDataIProps>({
    mode: 'onChange',
  })
  const isValid = watch('name') && watch('facility')

  const {
    create,
    handleOnClose,
    isCreateDeviceLoading,
    isDataLoading,
    facilityName,
  } = useCreateCheckpoint({ floorId: selectedFloorId, onClose })

  const onSubmit: SubmitHandler<FormDataIProps> = async (values) => {
    create(values.name)
  }

  return (
    <AlertDialog
      isCentered
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      motionPreset='slideInBottom'
      onClose={handleOnClose}
    >
      <AlertDialogOverlay data-testid='w:overlay' />

      <AlertDialogContent>
        <AlertDialogHeader>
          <HStack>
            <MdQrCode color='#44546F' size={30} />
            <Text
              color='#172B4D'
              fontSize='25px'
              fontWeight={700}
              letterSpacing='-0.53px'
              lineHeight='30px'
            >
              Create Checkpoint
            </Text>
          </HStack>
          <AlertDialogCloseButton
            data-testid='floorPlan_createCheckPointModal_close'
            mt='6px'
          />
        </AlertDialogHeader>
        <AlertDialogBody>
          <VStack spacing={3}>
            <FormInputControl
              data-testid='floorPlan_createCheckPointModal_name'
              errorMessage={errors.name && errors.name.message}
              id='name'
              inputRef={register({
                required: 'Checkpoint name is required',
              })}
              isInvalid={!!errors.name}
              label='Checkpoint Name'
              labelColor='#44546F'
              labelFontSize='14px'
              labelOpacity={1}
              placeholder='Enter Name'
              type='name'
            />
            <FormInputControl
              data-testid='floorPlan_createCheckPointModal_facility'
              errorMessage={errors.facility && errors.facility.message}
              id='facility'
              inputRef={register({
                required: 'Facility is required',
              })}
              isDisabled
              isInvalid={!!errors.facility}
              label='Facility'
              labelColor='#44546F'
              labelFontSize='14px'
              labelOpacity={1}
              placeholder='Enter Facility Name'
              value={facilityName}
            />
          </VStack>
        </AlertDialogBody>
        <AlertDialogFooter borderTop='1px solid #dddfe5' mt='16px'>
          <Button
            data-testid='floorPlan_createCheckPointModal_cancel'
            mr='3'
            onClick={handleOnClose}
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            data-testid='floorPlan_createCheckPointModal_confirm'
            isDisabled={isDataLoading || !isValid}
            isLoading={isCreateDeviceLoading}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            onClick={handleSubmit(onSubmit)}
            variant='primary'
          >
            Create
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
